import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import Logo from '@/assets/img/logo/logo.png';
import * as S from '@/components/Header/style';
import { headerMegaImages } from '@/constants/headerMegaImages';
import { normalizeInformationMenu } from '@/constants/information';
import { menuList, type MenuList } from '@/constants/menu';
import { normalizePublicationMenu, PUBLICATION_MENU_SUBMISSION } from '@/constants/publication';
import { normalizeSocietyMenu } from '@/constants/society';
import useIsMobile from '@/hooks/useIsMobile';
import { useScroll } from '@/hooks/useScroll';
import { useModalStore } from '@/store/modalStore';
import { useTokenStore } from '@/store/tokenStore';

import MobileMenu from './MobileMenu/MobileMenu';
import LanguageSwitcher from '../common/LanguageSwitcher/LanguageSwitcher';
import LinkIcon from '../Icon/LinkIcon';
import MenuIcon from '../Icon/MenuIcon';
import UserIcon from '../Icon/UserIcon';

const Header = () => {
	const { t } = useTranslation();
	const isScrolled = useScroll();
	const isMobile = useIsMobile(1180);
	const navigate = useNavigate();
	const { isLogin } = useTokenStore();
	const { pathname, state: locationState } = useLocation();
	const routeMenu = (locationState as { menu?: string } | null)?.menu;
	const [isOpen, setIsOpen] = useState(false);
	const [activeMenuIndex, setActiveMenuIndex] = useState<number | null>(null);
	const { showModal } = useModalStore();

	const activeMenu = activeMenuIndex !== null ? menuList[activeMenuIndex] : null;
	const hasMegaContent = Boolean(activeMenu?.megaKey);
	const isMegaOpen = !isMobile && activeMenuIndex !== null && hasMegaContent;

	const isAuthPath =
		pathname.split('/')[1].toLowerCase() === 'auth' ||
		pathname.split('/')[1].toLowerCase() === 'mypage' ||
		pathname.split('/')[1].toLowerCase() === 'conference';
	const isSolidHeader = isAuthPath || isMegaOpen || isScrolled;
	const textTone = isSolidHeader ? 'dark' : 'light';
	const iconColor = textTone === 'light' ? '#fff' : '#212529';

	const onClickSubMenu = (path: string, title: string) => {
		setActiveMenuIndex(null);
		if (title === '회원가입') {
			if (isLogin()) {
				showModal(t('이미 한국응용과학기술학회 회원이십니다.'), 'confirm');
				return;
			}
			navigate('/auth/signup');
			return;
		}
		if (title === '가입안내' || title === '연회비 납부') {
			if (!isLogin()) {
				showModal(t('common.alerts.loginRequired'), 'login', t('common.actions.goToLogin'), () => navigate('/auth/login'));
				return;
			}
		}
		navigate(path ?? '', { state: { menu: title } });
	};

	const getDefaultTabMenu = (megaKey?: string) => {
		switch (megaKey) {
			case 'introduce':
				return '학회소개';
			case 'society':
				return '학술행사 안내';
			case 'membership':
				return '가입안내';
			case 'publication':
				return '논문검색';
			case 'information':
				return '공지사항';
			default:
				return undefined;
		}
	};

	const isSameTabMenu = (megaKey: string | undefined, current: string, subKey: string) => {
		switch (megaKey) {
			case 'society':
				return normalizeSocietyMenu(current) === normalizeSocietyMenu(subKey);
			case 'publication':
				return normalizePublicationMenu(current) === normalizePublicationMenu(subKey);
			case 'information':
				return normalizeInformationMenu(current) === normalizeInformationMenu(subKey);
			default:
				return current === subKey;
		}
	};

	const isSubMenuActive = (subMenu: NonNullable<MenuList['subMenu']>[number], megaKey?: string, mainPath?: string) => {
		if (!subMenu.path || !mainPath || !pathname.startsWith(mainPath)) return false;
		const subKey = subMenu.menuKey ?? subMenu.title ?? '';
		const current = routeMenu ?? (megaKey && pathname.startsWith(mainPath) ? getDefaultTabMenu(megaKey) : undefined);
		if (!current || !subKey) return false;
		return isSameTabMenu(megaKey, current, subKey);
	};

	const handleMainMenuClick = (index: number) => {
		const menu = menuList[index];
		if (!menu.mainPath) return;
		setActiveMenuIndex(null);
		if (menu.mainMenuKey) {
			navigate(menu.mainPath, { state: { menu: menu.mainMenuKey } });
			return;
		}
		navigate(menu.mainPath, menu.mainPath === '/introduce' ? { state: { menu: '학회소개' } } : undefined);
	};

	const handleViewMore = () => {
		if (!activeMenu?.viewPath) return;
		const firstSub = activeMenu.subMenu?.[0];
		const firstMenu = firstSub?.menuKey ?? firstSub?.title;
		setActiveMenuIndex(null);
		navigate(activeMenu.viewPath, firstMenu ? { state: { menu: firstMenu } } : undefined);
	};

	const isMainMenuActive = (index: number) => {
		const menu = menuList[index];
		if (menu.mainPath && menu.mainPath !== 'link' && pathname.startsWith(menu.mainPath)) {
			if (menu.mainPath === '/publication') {
				const currentPublicationMenu = normalizePublicationMenu(routeMenu);
				if (menu.mainMenuKey) {
					return currentPublicationMenu === normalizePublicationMenu(menu.mainMenuKey);
				}
				if (menu.megaKey === 'publication') {
					return currentPublicationMenu !== normalizePublicationMenu(PUBLICATION_MENU_SUBMISSION);
				}
			}
			return true;
		}
		return menu.subMenu?.some((sub) => sub.path && pathname.startsWith(sub.path)) ?? false;
	};

	const renderMegaPanel = () => {
		if (!activeMenu?.megaKey) return null;
		const key = activeMenu.megaKey;

		return (
			<S.MegaPanel role="region" aria-label={t(activeMenu.mainMenu)}>
				<S.MegaIntro>
					<S.MegaLabel>{t(`header.mega.${key}.label`)}</S.MegaLabel>
					<S.MegaTitle>{t(`header.mega.${key}.title`)}</S.MegaTitle>
					<S.MegaDescription>{t(`header.mega.${key}.description`)}</S.MegaDescription>
				</S.MegaIntro>

				<S.MegaLinks>
					<S.MegaLinkList>
						{activeMenu.subMenu && activeMenu.subMenu.length > 0 ? (
							activeMenu.subMenu.map((subMenu, subIndex) => (
								<S.MegaLinkItem key={subIndex}>
									<S.MegaLinkButton
										type="button"
										$isActive={isSubMenuActive(subMenu, key, activeMenu.mainPath)}
										onClick={() =>
											onClickSubMenu(subMenu.path ?? '', subMenu.menuKey ?? subMenu.title ?? '')
										}
									>
										{subMenu.title ? t(subMenu.title) : ''}
									</S.MegaLinkButton>
								</S.MegaLinkItem>
							))
						) : (
							<S.MegaLinkItem>
								<S.MegaLinkButton type="button" onClick={() => handleMainMenuClick(activeMenuIndex ?? 0)}>
									{t(activeMenu.mainMenu)}
								</S.MegaLinkButton>
							</S.MegaLinkItem>
						)}
					</S.MegaLinkList>
					<S.MegaViewMore type="button" onClick={handleViewMore}>
						{t('header.mega.viewMore')}
					</S.MegaViewMore>
				</S.MegaLinks>

				<S.MegaVisual>
					<S.MegaVisualImage
						src={headerMegaImages[key] ?? headerMegaImages.introduce}
						alt={t(`header.mega.${key}.title`)}
						loading="lazy"
						decoding="async"
					/>
					<S.MegaVisualOverlay aria-hidden />
				</S.MegaVisual>
			</S.MegaPanel>
		);
	};

	const renderAuthItems = () =>
		!isMobile ? (
			<>
				<LanguageSwitcher />
				{isLogin() ? (
					<S.UserButton type="button" onClick={() => navigate('/mypage')}>
						<UserIcon width="20" height="20" />
						MY
					</S.UserButton>
				) : (
					<S.AuthButton $tone={textTone} type="button" onClick={() => onClickSubMenu('/auth/login', '로그인')}>
						{t('로그인')}
					</S.AuthButton>
				)}
			</>
		) : (
			<>
				<LanguageSwitcher />
				<MenuIcon iconColor={iconColor} onClick={() => toggleMobileMenu(true)} />
			</>
		);

	const toggleMobileMenu = (status: boolean) => {
		if (status) {
			setIsOpen(true);
			document.body.classList.add('hidden');
		} else {
			setIsOpen(false);
			document.body.classList.remove('hidden');
		}
	};
	const openMypage = () => {
		setIsOpen(false);
		document.body.classList.add('hidden');
		navigate('/mypage');
	};

	useEffect(() => {
		if (isOpen) {
			document.body.classList.remove('hidden');
			setIsOpen(false);
		}
	}, [isMobile]);

	useEffect(() => {
		setActiveMenuIndex(null);
	}, [pathname]);

	return (
		<>
			<S.HeaderContainer
				id="header"
				isMegaOpen={isMegaOpen}
				isAuthPath={isAuthPath}
				isScrolled={isScrolled}
				$solid={isSolidHeader}
				onMouseLeave={() => setActiveMenuIndex(null)}
			>
				<S.Nav aria-label={t('common.societyName')}>
					<S.TopBar>
						<S.LogoBox $lifted={isMegaOpen}>
							<Link to="/" onClick={() => setActiveMenuIndex(null)}>
								<S.ImgBox $tone={textTone}>
									<img src={Logo} alt={t('common.logo.alt')} decoding="async" />
								</S.ImgBox>
							</Link>
						</S.LogoBox>

						<S.MainMenu>
							{menuList.map((menu, index) => (
								<S.MainMenuItem
									key={index}
									type="button"
									$tone={textTone}
									$isActive={activeMenuIndex === index || (activeMenuIndex === null && isMainMenuActive(index))}
									data-active={activeMenuIndex === index ? 'true' : undefined}
									onMouseEnter={() => setActiveMenuIndex(index)}
									onClick={() => {
										if (menu.subMenu && menu.subMenu.length > 0) {
											setActiveMenuIndex(index);
											return;
										}
										handleMainMenuClick(index);
									}}
								>
									{menu.mainPath === 'link' ? (
										<>
											{t(menu.mainMenu)}
											<LinkIcon iconColor={iconColor} />
										</>
									) : (
										t(menu.mainMenu)
									)}
								</S.MainMenuItem>
							))}
						</S.MainMenu>

						<S.AuthBox isLogin={isLogin()}>{renderAuthItems()}</S.AuthBox>
					</S.TopBar>

					{isMegaOpen && renderMegaPanel()}
				</S.Nav>
			</S.HeaderContainer>

			<MobileMenu isOpen={isOpen} onClickIsOpen={() => toggleMobileMenu(false)} onOpenMypage={openMypage} />
		</>
	);
};

export default Header;
